"use client";

import { Minus, Plus } from "iconoir-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionPrimitive,
} from "@/components/ui/accordion";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

import type { BlogListingLabels, BlogPost } from "./data";

// Gradient sweep applies on hover for CLOSED rows only — an open row keeps
// its white surface so the title and toggle stay legible.
const HOVER_GRADIENT =
  "group-data-closed/row:hover:bg-[linear-gradient(-26deg,#FE634D_3%,#FB5170_11%,#E040B4_27%,#8A28F3_60%,#5F1FFE_84%,#001EFF_100%)]";

export function BlogAccordionList({
  posts,
  labels,
  className,
}: {
  posts: BlogPost[];
  labels: BlogListingLabels;
  className?: string;
}) {
  return (
    <Accordion className={cn("bg-grey-0", className)}>
      {posts.map((post) => (
        <AccordionItem
          key={post.slug}
          value={post.slug}
          className="group/row border-grey-100 border-b last:border-b-0"
        >
          <AccordionPrimitive.Header
            className={cn(
              "relative flex items-center justify-between gap-4 px-6 py-6 transition-colors duration-200 ease-out md:px-10",
              HOVER_GRADIENT
            )}
          >
            <Link
              href={`/blog/${post.slug}`}
              className="focus-visible:ring-pulse-700 flex min-w-0 flex-1 flex-col items-start gap-1 rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none md:flex-row md:items-center md:gap-4"
            >
              <span className="absolute inset-0" aria-hidden="true" />
              <span
                className={cn(
                  "text-ds-label-md text-grey-500 min-w-[70px] shrink-0 transition-colors duration-200 ease-out",
                  "group-hover/row:text-grey-200 group-data-open/row:!text-grey-500"
                )}
              >
                {post.dateLabel}
              </span>
              <span
                aria-hidden="true"
                className={cn(
                  "bg-grey-100 hidden h-5 w-px shrink-0 transition-colors duration-200 ease-out md:block",
                  "group-data-open/row:!bg-grey-100 group-hover/row:bg-white/20"
                )}
              />
              {post.author ? (
                <>
                  <span className="text-ds-label-md text-grey-950 group-hover/row:text-grey-0 group-data-open/row:!text-grey-950 hidden shrink-0 whitespace-nowrap transition-colors duration-200 ease-out md:group-data-open/row:inline">
                    {labels.author}: {post.author}
                  </span>
                  <span
                    aria-hidden="true"
                    className="bg-grey-100 group-data-open/row:!bg-grey-100 hidden h-5 w-px shrink-0 transition-colors duration-200 ease-out group-hover/row:bg-white/20 md:group-data-open/row:block"
                  />
                </>
              ) : null}
              <span
                className={cn(
                  "text-ds-body-sm text-grey-950 line-clamp-2 w-full min-w-0 transition-colors duration-200 ease-out md:line-clamp-none md:w-auto md:truncate",
                  "group-hover/row:text-grey-0 group-data-open/row:!text-grey-950"
                )}
              >
                {post.title}
              </span>
            </Link>

            <AccordionPrimitive.Trigger
              aria-label={post.togglePreviewLabel}
              className={cn(
                "bg-grey-0 border-grey-100 text-grey-950 rounded-ds-full relative z-10 grid size-7 shrink-0 cursor-pointer place-items-center border transition-colors duration-200 ease-out",
                "group-data-open/row:!bg-grey-0 group-hover/row:text-grey-0 group-data-open/row:!text-grey-950 group-hover/row:bg-white/15"
              )}
            >
              <Plus
                aria-hidden="true"
                className="size-3 group-data-open/row:hidden"
                strokeWidth={1.5}
              />
              <Minus
                aria-hidden="true"
                className="hidden size-3 group-data-open/row:block"
                strokeWidth={1.5}
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>

          <AccordionContent className="bg-grey-0 pb-0">
            <div className="flex flex-col items-start gap-5 px-6 pb-8 md:px-10">
              <p className="text-ds-body-sm text-grey-500 max-w-[899px]">
                {post.excerpt}
              </p>
              <Link
                href={`/blog/${post.slug}`}
                className="bg-grey-0 border-grey-100 text-ds-label-md text-grey-950 rounded-ds-full hover:bg-grey-50 focus-visible:ring-pulse-700 inline-flex w-fit items-center border px-4 py-2 font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                {labels.readArticle}
              </Link>
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
